import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Credit {
  id: number
  user_name: string | null
  user_id: number
  year: number | null
  hours: number
  reason: string | null
  created_at: string
}

interface Employee {
  id: number
  name: string
}

interface Props {
  credits: Credit[]
  employees: Employee[]
  current_year: number
}

export default function Index({ credits, current_year }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "PTO Requests", href: "/pto_requests" },
    { title: "PTO Credits", href: "/pto_credits" },
  ]

  // Group credits by year
  const creditsByYear = credits.reduce<Record<number, Credit[]>>(
    (acc, credit) => {
      const year = credit.year ?? current_year
      if (!acc[year]) acc[year] = []
      acc[year].push(credit)
      return acc
    },
    {},
  )

  const years = Object.keys(creditsByYear)
    .map(Number)
    .sort((a, b) => b - a)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="PTO Credits" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">PTO Credits</h1>
          <Link
            href="/pto_credits/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Credits
          </Link>
        </div>

        {years.length > 0 ? (
          years.map((year) => (
            <div key={year} className="mb-6">
              <h2 className="mb-2 text-lg font-semibold">{year} Credits</h2>
              <table className="border-hpi-light-gray w-full border-collapse border text-sm">
                <thead>
                  <tr className="bg-hpi-pale-gray">
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Employee
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-right">
                      Credit Hours
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Notes
                    </th>
                    <th className="border-hpi-light-gray border px-4 py-2 text-left">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {creditsByYear[year].map((credit) => (
                    <tr key={credit.id} className="hover:bg-hpi-pale-blue">
                      <td className="border-hpi-light-gray border px-4 py-2">
                        {credit.user_name}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2 text-right">
                        {credit.hours.toFixed(2)}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        {credit.reason ?? "-"}
                      </td>
                      <td className="border-hpi-light-gray border px-4 py-2">
                        <Link
                          href={`/pto_credits/${credit.id}/edit`}
                          className="text-hpi-active-blue hover:text-hpi-active-blue"
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        ) : (
          <p className="text-hpi-med-gray">No PTO credits found.</p>
        )}
      </div>
    </AppLayout>
  )
}
